import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import type { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { WatchlistTab } from '@terminal/views/insight-feed/components/manage-following-drawer/components/watchlist-tab/WatchlistTab';
import { ManageFollowingGeographiesTab } from '@terminal/views/insight-feed/components/manage-following-drawer/manage-following-geographies-tab/ManageFollowingGeographiesTab';
import { ManageFollowingThemesTab } from '@terminal/views/insight-feed/components/manage-following-drawer/manage-following-themes-tab/ManageFollowingThemesTab';
import {
  MANAGE_FOLLOWING_TABS,
  type ManageFollowingTab,
} from '@terminal/views/insight-feed/components/manage-following-drawer/useManageFollowingDrawer';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './FollowingConfiguration.module.scss';

export interface FollowingConfigurationProps {
  activeTab: ManageFollowingTab;
  headerSection: ReactNode;
  onTabChange: (tab: ManageFollowingTab) => void;
  trackingSource: PosthogTrackingSource;
  testId?: string;
  variant: 'drawer' | 'page';
}

export const FollowingConfiguration = ({
  activeTab,
  headerSection,
  onTabChange,
  trackingSource,
  testId,
  variant,
}: FollowingConfigurationProps) => {
  const { t } = useTranslation(['stream']);
  const tabLabels = [
    t('stream:manageFollowing.tabs.watchlist'),
    t('stream:manageFollowing.tabs.themes'),
    t('stream:manageFollowing.tabs.geographies'),
  ];

  const tabs = {
    watchlist: <WatchlistTab trackingSource={trackingSource} />,
    themes: <ManageFollowingThemesTab trackingSource={trackingSource} />,
    geographies: (
      <ManageFollowingGeographiesTab trackingSource={trackingSource} />
    ),
  };

  const handleTabChange = (index: number) => {
    onTabChange(MANAGE_FOLLOWING_TABS[index]);
  };

  return (
    <div
      className={clsx(styles.root, {
        [styles.page]: variant === 'page',
      })}
    >
      <div className={styles.header}>
        {headerSection}
        <div className={styles.tabs}>
          <ButtonGroup
            labels={tabLabels}
            activeIndex={MANAGE_FOLLOWING_TABS.indexOf(activeTab)}
            onClick={handleTabChange}
            data-testid="base-configuration-drawer-tabs"
          />
        </div>
      </div>
      <section
        className={styles.content}
        data-testid={testId ? `${testId}-content` : 'manage-following-content'}
        aria-live="polite"
      >
        {tabs[activeTab]}
      </section>
    </div>
  );
};
